import { useSnackbar } from "notistack";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import PropTypes from "prop-types";
import Form from "@accredo/fe-shared/components/Form/Form.jsx";
import FormTextField from "../../../components/Form/controls/FormTextField.jsx";
import SubmitButton from "@accredo/fe-shared/components/Form/controls/SubmitButton.jsx";

const schema = yup.object({
  name: yup.string().nullable(),
  email: yup
    .string()
    .email("Ungültige E-Mail-Adresse")
    .required("E-Mail-Adresse ist erforderlich"),
});

const DEFAULT_VALUES = { name: "", email: "" };

function InsurerContactFormModal({ open, onClose, contact, onCreate, onUpdate }) {
  const isEdit = Boolean(contact);
  const { enqueueSnackbar } = useSnackbar();
  const defaultValues = isEdit
    ? {
        name: contact.name ?? "",
        email: contact.email ?? "",
      }
    : DEFAULT_VALUES;

  async function handleSubmit(data, { setError }) {
    try {
      if (isEdit) {
        await onUpdate(contact.id, data);
        enqueueSnackbar("Kontakt gespeichert", { variant: "success" });
      } else {
        await onCreate(data);
        enqueueSnackbar("Kontakt angelegt", { variant: "success" });
      }
      onClose(true);
      return true;
    } catch (err) {
      const validation = err?.response?.data?.errors;
      if (validation) {
        Object.entries(validation).forEach(([field, messages]) => {
          setError(field, { message: messages[0] });
        });
      } else {
        enqueueSnackbar("Speichern fehlgeschlagen", { variant: "error" });
      }
    }
  }

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Kontakt bearbeiten" : "Neuer Kontakt"}</DialogTitle>

      <Form
        key={`${open}-${contact?.id ?? "new"}`}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        resolver={yupResolver(schema)}
        mode="onChange"
      >
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <FormTextField
              name="name"
              label="Name"
              helperText="Optional. Ohne Namen wird der Versicherer angezeigt."
              fullWidth
            />
            <FormTextField
              name="email"
              label="E-Mail-Adresse"
              type="email"
              required
              fullWidth
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box flexGrow={1} />
          <DsButton variant="tertiary" onClick={() => onClose(false)}>
            Abbrechen
          </DsButton>
          <SubmitButton requireDirty={false}>Speichern</SubmitButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
}

InsurerContactFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contact: PropTypes.object,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default InsurerContactFormModal;
